import axios from 'axios'
import { API_BASE } from '../config'

const WIKI_API = 'https://oldschool.runescape.wiki/api.php';

function authHeaders() {
    return { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
}

// Every request resolves to the whole event as it now stands:
// { rows, columns, startsAt, endsAt, tiles: [{ title, points, image, tiers: [{ task, points }] }] (all rows x columns
//   of them, from the top left, row by row), teams: [{ id, name, color, members: [{ id, username }], completed,
//   tiers: { tile: [tier] } }] } (members: the players on the team, whose drops go in for it)
// where startsAt and endsAt are when the event starts and ends (both null until they are set),
// image is the address of the tile's picture (empty if it has none) and completed lists the numbers of the
// tiles that team has done anything of. A tile's tiers are bronze first (empty if it is done all at once); a team's
// tiers say which of a tiered tile's they have done (1 bronze, 2 silver, 3 gold). color is the "#rrggbb" a team is
// drawn in
export default class Bingo {
    // GET /bingo
    getBoard() {
        return axios.get(`${API_BASE}/bingo`).then(x => x.data);
    }

    // The rest are for Admins and Founders

    // PUT /bingo/tiles/:tile - Set what a tile says, shows and is worth
    // (tiers: up to three { task, points }, bronze first, or empty for a tile done all at once)
    setTile(tile, title, points, image, tiers = []) {
        return axios.put(`${API_BASE}/bingo/tiles/${tile}`, { title, points, image, tiers }, authHeaders()).then(x => x.data);
    }

    // POST /bingo/teams - Add a team, with the colour it is drawn in ("#rrggbb")
    addTeam(name, color) {
        return axios.post(`${API_BASE}/bingo/teams`, { name, color }, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/teams/:id - Change a team's name and/or colour: changes is either or both of { name, color }
    updateTeam(id, changes) {
        return axios.put(`${API_BASE}/bingo/teams/${encodeURIComponent(id)}`, changes, authHeaders()).then(x => x.data);
    }

    // DELETE /bingo/teams/:id - Remove a team for good
    deleteTeam(id) {
        return axios.delete(`${API_BASE}/bingo/teams/${encodeURIComponent(id)}`, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/teams/:id/tiles/:tile - Mark a tile as completed by a team, or not
    // (tier: which of a tiered tile's tiers, 1 bronze to 3 gold; 0 for a tile without tiers)
    setCompleted(id, tile, completed, tier = 0) {
        return axios.put(`${API_BASE}/bingo/teams/${encodeURIComponent(id)}/tiles/${tile}`, { completed, tier }, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/teams/:id/members - Put a player on a team by their clomp username (off any other team)
    addMember(teamId, username) {
        return axios.put(`${API_BASE}/bingo/teams/${encodeURIComponent(teamId)}/members`, { username }, authHeaders()).then(x => x.data);
    }

    // DELETE /bingo/teams/:id/members/:user - Take a player (by user id) off a team
    removeMember(teamId, userId) {
        return axios.delete(`${API_BASE}/bingo/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, authHeaders()).then(x => x.data);
    }

    // POST /bingo/submissions - Send in a drop for an admin to check (any logged-in player): { teamId, tile, tier,
    // image, note }. Resolves to { id }; a refusal's response says why: { message }
    submitDrop(drop) {
        return axios.post(`${API_BASE}/bingo/submissions`, drop, authHeaders()).then(x => x.data);
    }

    // GET /bingo/submissions/mine - The logged-in player's drops waiting to be checked: { submissions }
    getMySubmissions() {
        return axios.get(`${API_BASE}/bingo/submissions/mine`, authHeaders()).then(x => x.data);
    }

    // GET /bingo/submissions - Every drop waiting to be checked (admins):
    // { submissions: [{ id, teamId, team, color, tile, tier, image, note, by: { id, username }, createdAt }] }
    getSubmissions() {
        return axios.get(`${API_BASE}/bingo/submissions`, authHeaders()).then(x => x.data);
    }

    // POST /bingo/submissions/:id/approve - Approve a drop: the event (as getBoard), with { submissions } still waiting
    approveSubmission(id) {
        return axios.post(`${API_BASE}/bingo/submissions/${encodeURIComponent(id)}/approve`, {}, authHeaders()).then(x => x.data);
    }

    // DELETE /bingo/submissions/:id - Deny a drop: { submissions } still waiting
    denySubmission(id) {
        return axios.delete(`${API_BASE}/bingo/submissions/${encodeURIComponent(id)}`, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/bonus - Set the bonus for completing a whole row or column: [bronze, silver, gold] points
    setLineBonus(points) {
        return axios.put(`${API_BASE}/bingo/bonus`, { points }, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/title - Rename the event
    setTitle(title) {
        return axios.put(`${API_BASE}/bingo/title`, { title }, authHeaders()).then(x => x.data);
    }

    // PUT /bingo/times - Set when the event starts and ends (dates as text), or pass two nulls for no times
    setTimes(startsAt, endsAt) {
        return axios.put(`${API_BASE}/bingo/times`, { startsAt, endsAt }, authHeaders()).then(x => x.data);
    }

    // POST /bingo/rows - Add a row of blank tiles to the bottom of the board
    addRow() {
        return axios.post(`${API_BASE}/bingo/rows`, {}, authHeaders()).then(x => x.data);
    }

    // POST /bingo/columns - Add a column of blank tiles to the right of the board
    addColumn() {
        return axios.post(`${API_BASE}/bingo/columns`, {}, authHeaders()).then(x => x.data);
    }

    // DELETE /bingo/rows/:number - Take a row (0 is the top one) off the board for good, with its tiles
    removeRow(row) {
        return axios.delete(`${API_BASE}/bingo/rows/${row}`, authHeaders()).then(x => x.data);
    }

    // DELETE /bingo/columns/:number - Take a column (0 is the left one) off the board for good, with its tiles
    removeColumn(column) {
        return axios.delete(`${API_BASE}/bingo/columns/${column}`, authHeaders()).then(x => x.data);
    }

    // Look up Old School RuneScape Wiki pages whose names start with some text, for their pictures. This asks the
    // wiki itself, not the clomp backend. Resolves to the pages that have a picture, best match first:
    // [{ title, image }]
    searchWiki(text) {
        return axios.get(WIKI_API, {
            params: {
                action: 'query', format: 'json', origin: '*', generator: 'prefixsearch', gpssearch: text, gpslimit: 12,
                prop: 'pageimages', piprop: 'thumbnail', pithumbsize: 160
            }
        }).then((x) => {
            let pages = Object.values((x.data.query && x.data.query.pages) || {});
            return pages.filter((page) => page.thumbnail).sort((a, b) => a.index - b.index)
                .map((page) => ({ title: page.title, image: page.thumbnail.source }));
        });
    }
}
